import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SpeechService, SpeechConfig } from '@utils/azure-speech';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import clsx from 'clsx';
import { differenceInDays } from 'date-fns';

interface VoiceInputProps {
  onTranscription: (text: string) => void;
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (errorMessage: string) => void;
  maxDuration?: number;
}

export const VoiceInput: React.FC<VoiceInputProps> = ({
  onTranscription,
  onStart = () => {},
  onEnd = () => {},
  onError = () => {},
  maxDuration = 14
}) => {
  const { tier, incrementUsage, remainingUses, usageResetDate } = useSubscription();
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(maxDuration);
  
  const speechServiceRef = useRef<SpeechService | null>(null);
  const timerRef = useRef<NodeJS.Timeout>();

  const handleRecognition = useCallback((text: string) => {
    onTranscription(text);
  }, [onTranscription]);

  const stopRecording = async () => {
    try {
      if (speechServiceRef.current) {
        await speechServiceRef.current.stopRecognizing();
        speechServiceRef.current.dispose();
        speechServiceRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      setIsRecording(false);
      setTimeLeft(maxDuration);
      onEnd();
      
      // Increment usage count after successful recording
      try {
        await incrementUsage();
      } catch (err) {
        console.error('Error incrementing usage:', err);
      }
    } catch (err) {
      console.error('Error stopping recording:', err);
      setError('Failed to stop recording properly');
      onError('Failed to stop recording properly');
    }
  };

  const startRecording = async () => {
    try {
      // Check if we can proceed before starting
      const canProceed = remainingUses > 0;
      if (!canProceed) {
        console.log('Usage limit reached');
        setError('Usage limit reached');
        onError('Usage limit reached');
        return;
      }

      const config: SpeechConfig = {
        key: import.meta.env.VITE_AZURE_SPEECH_KEY,
        region: import.meta.env.VITE_AZURE_SPEECH_REGION,
        language: 'en-US',
        tier
      };

      speechServiceRef.current = SpeechService.getInstance(config);
      speechServiceRef.current.onRecognized(handleRecognition);
      await speechServiceRef.current.startRecognizing();
      
      setIsRecording(true);
      setTimeLeft(maxDuration);
      onStart();
      
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            stopRecording();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      console.error('Error starting recording:', err);
      setError('Failed to start recording');
      onError('Failed to start recording');
      if (speechServiceRef.current) {
        speechServiceRef.current.dispose();
        speechServiceRef.current = null;
      }
    }
  };

  const handleClick = async () => {
    if (isRecording) {
      await stopRecording();
    } else {
      await startRecording();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (speechServiceRef.current) {
        speechServiceRef.current.dispose();
        speechServiceRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  return (
    <div className="voice-input">
      <button
        type="button"
        onClick={handleClick}
        disabled={remainingUses === 0 && !usageResetDate}
        className={clsx(
          "w-full px-6 py-3 rounded-full font-medium text-white transition-all duration-200 transform hover:scale-105 flex items-center justify-center space-x-2",
          {
            "bg-red-500 hover:bg-red-600": isRecording,
            "bg-blue-500 hover:bg-blue-600": !isRecording
          }
        )}
      >
        {isRecording ? (
          <>
            <span className="w-3 h-3 rounded-full bg-red-500" />
            <span>Stop Recording... {timeLeft}s</span>
          </>
        ) : (
          <>
            <span className="w-3 h-3 rounded-full bg-white" />
            <span>Start Recording</span>
          </>
        )}
      </button>

      <div className="flex items-center justify-end text-sm italic text-gray-600 mt-2">
        <span className="font-medium">{remainingUses}</span>
        <span className="ml-1">uses remaining</span>
        {usageResetDate && (
          <span className="ml-2">
            · resets in {Math.max(0, differenceInDays(new Date(usageResetDate), new Date()))} days
          </span>
        )}
      </div>

      {error && (
        <div className="flex items-center text-sm text-red-500 mt-2">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-2 text-blue-500 hover:text-blue-600 underline"
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
};